<template>
  <div id="reset-password">
    <div class="reset-wrap">
      <v-card class="reset-card">
        <img src="@/assets/logoFta.png" alt="FTA" class="logo" />

        <!-- Invalid / missing token -->
        <div v-if="invalidLink" class="state-block" data-cy="invalid-link">
          <v-icon size="40" color="#e53935">mdi-link-off</v-icon>
          <p class="state-msg state-msg--error">Lien invalide ou expiré.</p>
          <router-link :to="{ name: 'login' }" class="btn-link">
            Retour à la connexion
          </router-link>
        </div>

        <!-- Success -->
        <div v-else-if="success" class="state-block">
          <v-icon size="40" color="#2e7d32">mdi-check-circle-outline</v-icon>
          <p class="state-msg state-msg--success">Mot de passe mis à jour.</p>
          <p class="state-sub">Redirection en cours…</p>
        </div>

        <!-- Reset form -->
        <div v-else>
          <p class="reset-intro">Choisissez un nouveau mot de passe.</p>

          <v-text-field
            v-model="password"
            outlined
            dense
            data-cy="password-field"
            label="Nouveau mot de passe"
            placeholder=" "
            :type="showPassword ? 'text' : 'password'"
            prepend-inner-icon="mdi-lock-outline"
            :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            hide-details="auto"
            :error-messages="passwordError"
            class="mb-3"
            @click:append="showPassword = !showPassword"
            @input="passwordError = ''"
          />

          <v-text-field
            v-model="confirm"
            outlined
            dense
            data-cy="confirm-field"
            label="Confirmer le mot de passe"
            placeholder=" "
            :type="showConfirm ? 'text' : 'password'"
            prepend-inner-icon="mdi-lock-check-outline"
            :append-icon="showConfirm ? 'mdi-eye-off' : 'mdi-eye'"
            hide-details="auto"
            :error-messages="confirmError"
            @click:append="showConfirm = !showConfirm"
            @input="confirmError = ''"
          />

          <p v-if="serverError" class="server-error mt-3" data-cy="server-error">{{ serverError }}</p>

          <v-btn
            block
            depressed
            rounded
            dark
            :loading="loading"
            data-cy="submit-btn"
            class="btn-submit mt-5"
            @click="submit"
          >
            Réinitialiser le mot de passe
          </v-btn>

          <div class="back-row mt-4">
            <router-link :to="{ name: 'login' }" class="btn-link">
              Retour à la connexion
            </router-link>
          </div>
        </div>
      </v-card>

      <div class="barre-bleue" />
    </div>
  </div>
</template>

<script>
import api from "@/api/user";

const PASSWORD_REGEX = /^(?=.*[a-zA-Z])(?=.*\d).{8,}$/;
const PASSWORD_ERROR_MSG = "Le mot de passe doit contenir au moins 8 caractères, dont une lettre et un chiffre.";

export default {
  name: "ResetPassword",
  data() {
    return {
      token: null,
      id: null,
      invalidLink: false,
      password: "",
      confirm: "",
      showPassword: false,
      showConfirm: false,
      passwordError: "",
      confirmError: "",
      serverError: "",
      loading: false,
      success: false,
    };
  },
  created() {
    const { token, id } = this.$route.query;
    if (!token || !id) {
      this.invalidLink = true;
      return;
    }
    this.token = token;
    this.id = id;
  },
  methods: {
    validate() {
      let valid = true;
      if (!PASSWORD_REGEX.test(this.password)) {
        this.passwordError = PASSWORD_ERROR_MSG;
        valid = false;
      }
      if (this.password !== this.confirm) {
        this.confirmError = "Les mots de passe ne correspondent pas.";
        valid = false;
      }
      return valid;
    },
    submit() {
      this.serverError = "";
      if (!this.validate()) { return; }
      this.loading = true;
      api.resetPassword(this.id, this.token, this.password)
        .then(() => {
          this.success = true;
          setTimeout(() => this.$router.push({ name: "login" }), 2500);
        })
        .catch((error) => {
          this.loading = false;
          const status = error.response?.status;
          const msg = error.response?.data?.message || "";
          if (status === 400) {
            if (msg.toLowerCase().includes("mot de passe")) {
              this.passwordError = msg;
            } else {
              this.invalidLink = true;
            }
          } else {
            this.serverError = "Une erreur est survenue. Veuillez réessayer.";
          }
        });
    },
  },
};
</script>

<style scoped lang="scss">
#reset-password {
  min-height: 100vh;
  background: linear-gradient(160deg, #f3f2f0 55%, #e1edf2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.reset-wrap {
  width: 100%;
  max-width: 440px;
  padding: 16px;
}

.reset-card {
  padding: 32px 28px 24px;
  border-radius: 20px 20px 0 0 !important;
}

.logo {
  display: block;
  width: 70%;
  margin: 0 auto 28px;
}

.reset-intro {
  font-size: 0.85rem;
  color: #444;
  margin-bottom: 18px;
}

.state-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px 0 8px;
}

.state-msg {
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0;

  &--error { color: #e53935; }
  &--success { color: #2e7d32; }
}

.state-sub {
  font-size: 0.8rem;
  color: #777;
  margin: 0;
}

.server-error {
  color: #e53935;
  font-size: 0.78rem;
  margin: 0;
}

.back-row {
  text-align: center;
}

.btn-link {
  color: #004196;
  font-size: 0.8rem;
  text-decoration: underline;

  &:hover { color: #0b87ba; }
}

.btn-submit {
  background-color: #ff9900 !important;
  letter-spacing: 0.03em;
}

.barre-bleue {
  background-color: #2887b9;
  height: 9px;
  box-shadow: 0 8px 0 #c2d8df;
  border-radius: 0 0 6px 6px;
}

::v-deep .v-label {
  color: #004196 !important;
}

::v-deep .v-input--is-focused fieldset {
  border-color: #0b87ba !important;
}
</style>
